/**
 * Standalone definitions for the four WAC Refresh Q4 FY26 experiments.
 *
 * These are intentionally kept OUT of `./manifest.ts` so that consumers
 * (notably `wac-refresh-context.tsx`, which is imported by foundation
 * components like `column-layout.tsx`) can reference them without
 * transitively pulling in the manifest's `@/components/user-context`
 * import — which causes a circular module-evaluation crash in tests
 * that load `signup/utils` while it is mid-evaluation.
 *
 * The manifest re-exports these so the rest of the codebase continues
 * to access them via `experiments.wacRefreshJira` etc. unchanged.
 *
 * Note: the `cohort` callbacks intentionally do NOT import from
 * `@/components/user-context` (which would re-introduce the circular
 * dependency). Instead we inline a local copy of `useSpecifiedPathnames`
 * — a 4-line helper that only reads the current pathname from the
 * location provider — see source at
 * `services/wac-web/src/components/user-context/helpers/cohorts.ts`.
 */

import { useLocation } from '@/utils/location-provider';

import type { Experiment } from './use-experiment';

const booleanTypeGuard = (value: unknown): value is boolean => typeof value === 'boolean';

/**
 * Local copy of `useSpecifiedPathnames` from
 * `@/components/user-context/helpers/cohorts` — inlined here to avoid
 * re-introducing the circular import chain that this whole file exists
 * to break. Behaviour must stay in sync with the source.
 */
const useSpecifiedPathnames = (pathnames: string[]) => {
	const { pathname: currentPathname } = useLocation();
	return pathnames.includes(currentPathname);
};

export const wacRefreshJira: Experiment<boolean> = {
	experimentName: 'wac-refresh-jira',
	parameterName: 'isEnabled',
	defaultValue: false,
	options: {
		fireExperimentExposure: true,
		typeGuard: booleanTypeGuard,
	},
	targeting: {
		cohort: () => {
			// oxlint-disable-next-line react-hooks/rules-of-hooks
			return useSpecifiedPathnames(['/software/jira']);
		},
	},
	metadata: {
		description: '50/50 A/B test of the WAC Refresh design on the Jira Product Tour page',
		expectedReleaseDate: '06/02/2026',
		expectedRuntime: '19 days',
		context: {
			urls: ['/software/jira'],
		},
	},
};

export const wacRefreshConfluence: Experiment<boolean> = {
	experimentName: 'wac-refresh-confluence',
	parameterName: 'isEnabled',
	defaultValue: false,
	options: {
		fireExperimentExposure: true,
		typeGuard: booleanTypeGuard,
	},
	targeting: {
		cohort: () => {
			// oxlint-disable-next-line react-hooks/rules-of-hooks
			return useSpecifiedPathnames(['/software/confluence']);
		},
	},
	metadata: {
		description: '50/50 A/B test of the WAC Refresh design on the Confluence Product Tour page',
		expectedReleaseDate: '06/02/2026',
		expectedRuntime: '21 days',
		context: {
			urls: ['/software/confluence'],
		},
	},
};

export const wacRefreshHomepage: Experiment<boolean> = {
	experimentName: 'wac-refresh-homepage',
	parameterName: 'isEnabled',
	defaultValue: false,
	options: {
		fireExperimentExposure: true,
		typeGuard: booleanTypeGuard,
	},
	targeting: {
		cohort: () => {
			// oxlint-disable-next-line react-hooks/rules-of-hooks
			return useSpecifiedPathnames(['/']);
		},
	},
	metadata: {
		description: '50/50 A/B test of the WAC Refresh design on the Atlassian homepage',
		expectedReleaseDate: '06/02/2026',
		expectedRuntime: '23 days',
		context: {
			urls: ['/'],
		},
	},
};

export const wacRefreshTwcRovo: Experiment<boolean> = {
	experimentName: 'wac-refresh-twc-rovo',
	parameterName: 'isEnabled',
	defaultValue: false,
	options: {
		fireExperimentExposure: true,
		typeGuard: booleanTypeGuard,
	},
	targeting: {
		cohort: () => {
			// oxlint-disable-next-line react-hooks/rules-of-hooks
			return useSpecifiedPathnames(['/software/rovo', '/collections/teamwork']);
		},
	},
	metadata: {
		description:
			'50/50 A/B test of the WAC Refresh design on the Rovo and Teamwork Collection pages',
		expectedReleaseDate: '06/02/2026',
		expectedRuntime: 'TBD',
		context: {
			urls: ['/software/rovo', '/collections/teamwork'],
		},
	},
};

export const wacRefreshJsm: Experiment<boolean> = {
	experimentName: 'jsmgrowth_535_preview_playground',
	parameterName: 'isEnabled',
	defaultValue: false,
	options: {
		fireExperimentExposure: true,
		typeGuard: booleanTypeGuard,
	},
	targeting: {
		cohort: () => {
			// oxlint-disable-next-line react-hooks/rules-of-hooks
			return useSpecifiedPathnames(['/software/jira/service-management']);
		},
	},
	metadata: {
		description:
			'50/50 A/B test, new JSM product tour with preview playground will see the refreshed page',
		expectedReleaseDate: '10/22/2026',
		expectedRuntime: '30 days',
		context: {
			urls: ['/software/jira/service-management'],
		},
	},
};
